import { Level2OuterPageLoading, useSetInPageNavVisible } from "@octopusdeploy/design-system-components";
import { StringHelper } from "@octopusdeploy/design-system-octopus-components";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import { type PropsWithChildren, type ReactNode } from "react";
import * as React from "react";
import type { Location } from "react-router-dom";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { repository } from "~/clientInstance";
export interface RedirectProjectIfNotSlugProps {
    children: ReactNode;
}
export function RedirectProjectIfNotSlug({ children }: PropsWithChildren<RedirectProjectIfNotSlugProps>) {
    const setVisible = useSetInPageNavVisible();
    React.useEffect(() => {
        setVisible?.(true);
    }, [setVisible]);
    return (<SlugSafeRedirect parameter={"projectSlug"} regexp={/^Projects-[0-9]+$/} getRealParam={loadSlugFromProjectId} loadingComponent={<ProjectSlugLoading />}>
            {children}
        </SlugSafeRedirect>);
}
function ProjectSlugLoading() {
    return (<main>
            <Level2OuterPageLoading />
        </main>);
}
interface ProjectLocationState {
    project?: {
        Slug: string;
    };
}
async function loadSlugFromProjectId(projectId: string, location: Location) {
    if (locationHasProjectState(location)) {
        return location.state.project.Slug;
    }
    return (await repository.Projects.get(projectId)).Slug;
}
function locationHasProjectState(location: Location): location is Location<Required<ProjectLocationState>> {
    if (!location || !location?.state) {
        return false;
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const casted = location as Location<ProjectLocationState>;
    if (!casted.state?.project) {
        return false;
    }
    return typeof casted.state.project.Slug === "string";
}
export function RedirectReleaseVersion({ children }: React.PropsWithChildren<{}>) {
    return (<SlugSafeRedirect parameter="releaseVersion" regexp={/^Releases-[0-9]+$/} getRealParam={loadVersionFromReleaseId} loadingComponent={releaseVersionLoading()}>
            {children}
        </SlugSafeRedirect>);
}
async function loadVersionFromReleaseId(releaseId: string) {
    return (await repository.Releases.get(releaseId)).Version;
}
function releaseVersionLoading() {
    return <div>{StringHelper.ellipsis}</div>;
}
type SlugSafeRedirectProps<T extends string> = {
    parameter: T;
    regexp: RegExp;
    getRealParam(param: string, location?: Location): Promise<string>;
    loadingComponent: React.ReactElement;
    children: React.ReactNode;
};
export function SlugSafeRedirect<ParamName extends string>({ parameter, regexp, getRealParam, loadingComponent, children }: SlugSafeRedirectProps<ParamName>) {
    const location = useLocation();
    const params = useParams<{
        [key in ParamName | "*"]: string | undefined;
    }>();
    const paramValue = params[parameter];
    const splat = params["*"];
    const shouldTryRedirect = paramValue ? regexp.test(paramValue) : false;
    const { result: targetParamValue, isLoading } = useInlineStatusQuery(async () => {
        if (!shouldTryRedirect || !paramValue) {
            return undefined;
        }
        return getRealParam(paramValue, location);
    }, 
    // We only want this query to execute once
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [], "SlugSafeRedirect real parameter", { initialResult: shouldTryRedirect ? undefined : paramValue });
    if (targetParamValue && paramValue !== targetParamValue) {
        const redirectPath = targetParamValue ? `../${targetParamValue}${splat ? `/${splat}` : ""}` : undefined;
        const state = {
            ...(typeof location.state === "object" ? location.state : {}),
            [parameter]: targetParamValue,
        };
        const redirectTo = {
            pathname: redirectPath,
            search: location.search,
            hash: location.hash,
            state,
        };
        /* eslint-disable react/forbid-elements */
        return <Navigate to={redirectTo} replace={true} relative={"path"}/>;
    }
    return isLoading ? loadingComponent : <>{children}</>;
}
